import React from 'react';

import classnames from 'classnames';

import styles from './Col.module.scss';

type GenerateRange<N extends number, Arr extends number[] = []> = Arr['length'] extends N
    ? Arr[number]
    : GenerateRange<N, [...Arr, Arr['length']]>;

export type ColumnRange = GenerateRange<25> | number;

export type ColProps = React.HTMLAttributes<HTMLDivElement> & {
    /*
     * Number of columns to span
     */
    span?: ColumnRange;
    /*
     * Number of columns to offset
     */
    offset?: ColumnRange;
    /*
     * Flex order
     */
    order?: ColumnRange;
    /*
     * Push column to the right
     */
    push?: ColumnRange;
    /*
     * Pull column to the left
     */
    pull?: ColumnRange;
    /*
     * Span for extra small screens (<576px)
     */
    xs?: ColumnRange;
    /*
     * Span for small screens (≥576px)
     */
    sm?: ColumnRange;
    /*
     * Span for medium screens (≥768px)
     */
    md?: ColumnRange;
    /*
     * Span for large screens (≥992px)
     */
    lg?: ColumnRange;
    /*
     * Span for extra large screens (≥1200px)
     */
    xl?: ColumnRange;
    /*
     * Span for extra extra large screens (≥1600px)
     */
    xxl?: ColumnRange;
    /*
     * CSS flex value (e.g., '40px', '1 0 200px', 'auto')
     * Note: https://developer.mozilla.org/en-US/docs/Web/CSS/flex#syntax
     */
    flex?: string;
    /*
     * Display type
     */
    type?: 'flex';
    className?: string;
    style?: React.CSSProperties;
    children?: React.ReactNode;
};

export const Col = ({
    span,
    offset,
    order,
    push,
    pull,
    xs,
    sm,
    md,
    lg,
    xl,
    xxl,
    flex,
    type,
    className,
    style,
    children,
    ...rest
}: ColProps) => {
    const computedStyles: React.CSSProperties = { ...style };

    if (flex) {
        computedStyles.flex = flex;
    }

    const colClasses = classnames(
        styles.col,
        {
            // Main grid properties
            [styles[`col-span-${span ?? xs}`]]: span !== undefined || xs !== undefined,
            [styles[`col-offset-${offset}`]]: offset,
            [styles[`col-order-${order}`]]: order,
            // Position adjustments
            [styles[`col-push-${push}`]]: push,
            [styles[`col-pull-${pull}`]]: pull,
            // Responsive breakpoints
            [styles[`col-xs-${xs}`]]: xs,
            [styles[`col-sm-${sm}`]]: sm,
            [styles[`col-md-${md}`]]: md,
            [styles[`col-lg-${lg}`]]: lg,
            [styles[`col-xl-${xl}`]]: xl,
            [styles[`col-xxl-${xxl}`]]: xxl,
            // Display type
            [styles.flex]: type === 'flex'
        },
        className
    );

    return (
        <div className={colClasses} style={computedStyles} {...rest}>
            {children}
        </div>
    );
};
